import { defineStore } from 'pinia';
import { gql } from '@urql/vue';

import  {
  type AddCalendarEventInput,
  type AddCalendarEventsInput,
  type AddItemToCalendarInput,
  CalendarDaysDocument,
  type CalendarEvent,
  CalendarEventType,
  type CreateCalendarNoteInput,
  type ItemInstance,
  type MoveCalendarEventInput,
  type RemoveCalendarEventInput,
  type UpdateCalendarNoteInput
} from "~/graphql";

import { useFeaturesManagement } from "~/stores/featuresManagement";
import type { WeeklyPlanningQueryKeys } from "~/utils/weeklyPlanningUtils";
import {
  useAddCalendarEventMutation,
  useAddCalendarEventsMutation,
  useAddCalendarNoteMutation,
  useAddItemToCalendarMutation,
  useMoveCalendarEventMutation,
  useRemoveCalendarEventMutation,
  useUpdateCalendarNoteMutation
} from "~/composables";

const addCalendarEventHelper = (
  queryKey: WeeklyPlanningQueryKeys,
  cache,
  calendarEventResult: ItemInstance,
  variables: {
    position: number;
    courseId: string;
    calendarDayId: string;
  },
) => {
  const formattedFirstDay = queryKey.fromDay;
  const formattedLastDay = queryKey.toDay;
  const position = variables.position;
  const courseId = variables.courseId;
  const calendarDayId = variables.calendarDayId;

  cache.updateQuery(
    {
      query: CalendarDaysDocument,
      variables: {
        calendarDaysInput: {
          courseId: courseId,
          fromDay: formattedFirstDay,
          toDay: formattedLastDay,
        },
      },
    },
    (data) => {
      if (data) {
        const targetIndex = data.calendarDays.findIndex(
          (day) => day.id === calendarDayId,
        );
        if (targetIndex !== -1) {
          const cloneEvents = [
            ...data.calendarDays[targetIndex].calendarEvents,
          ];
          // add itemInstance to the calendarDay
          if (position === -1) {
            cloneEvents.push(calendarEventResult);
          } else {
            cloneEvents.splice(position, 0, calendarEventResult);
          }
          data.calendarDays[targetIndex].calendarEvents = cloneEvents;
        }
      }
      return data;
    },
  );

  const fragment = gql`
    fragment _ on ItemInstance {
      id
      dates
    }
  `;
  // update itemInstance to include the new date

  cache.writeFragment(fragment, {
    id: calendarEventResult.id,
    dates: calendarEventResult.dates,
  });
};

export const weeklyPlanningManagementCacheExchange = {
  optimistic: {
    removeCalendarEvent() {
      return {
        __typename: 'DoneResult',
        done: true,
      };
    },
  },
  updates: {
    Mutation: {
      addCalendarEvent(result, _args, cache) {
        const weeklyManagement = useWeekPlanningManagement();
        const keys: WeeklyPlanningQueryKeys = {
          fromDay:
            weeklyManagement.targetQueryStartDate !== ''
              ? weeklyManagement.targetQueryStartDate
              : _args.addCalendarEventInput.day,
          toDay:
            weeklyManagement.targetQueryEndDate !== ''
              ? weeklyManagement.targetQueryStartDate
              : _args.addCalendarEventInput.day,
        };
        const variables = {
          position: _args.addCalendarEventInput.calendarEventDetails.position,
          courseId: _args.addCalendarEventInput.courseId,
          calendarDayId: _args.addCalendarEventInput.calendarDayId,
        };

        addCalendarEventHelper(
          keys,
          cache,
          {
            ...result.addCalendarEvent.calendarEvent,
            typename: 'ItemInstance',
          },
          variables,
        );
      },

      addItemToCalendar(result, _args, cache) {
        const weeklyManagement = useWeekPlanningManagement();
        const keys: WeeklyPlanningQueryKeys = {
          fromDay:
            weeklyManagement.targetQueryStartDate !== ''
              ? weeklyManagement.targetQueryStartDate
              : _args.addItemToCalendarInput.day,
          toDay:
            weeklyManagement.targetQueryEndDate !== ''
              ? weeklyManagement.targetQueryStartDate
              : _args.addItemToCalendarInput.day,
        };
        const variables = {
          position: _args.addItemToCalendarInput.position,
          courseId: _args.addItemToCalendarInput.courseId,
          calendarDayId: _args.addItemToCalendarInput.calendarDayId,
        };
        addCalendarEventHelper(
          keys,
          cache,
          {
            ...result.addItemToCalendar.calendarEvent,
            typename: 'ItemInstance',
          },
          variables,
        );
      },

      addCalendarEvents(result, _args, cache) {
        const weeklyManagement = useWeekPlanningManagement();
        const formatedFirstDay = weeklyManagement.targetQueryStartDate;
        const formatedLastDay = weeklyManagement.targetQueryEndDate;
        const courseId = _args.addCalendarEventsInput.courseId;

        const calendarDayId = _args.addCalendarEventsInput.calendarDayId;

        cache.updateQuery(
          {
            query: CalendarDaysDocument,
            variables: {
              calendarDaysInput: {
                courseId: courseId,
                fromDay: formatedFirstDay,
                toDay: formatedLastDay,
              },
            },
          },
          (data) => {
            if (data) {
              const targetIndex = data.calendarDays.findIndex(
                (day) => day.id === calendarDayId,
              );
              data.calendarDays[targetIndex].calendarEvents = [
                ...data.calendarDays[targetIndex].calendarEvents,
                ...result.addCalendarEvents.calendarEvents,
              ];
            }

            return data;
          },
        );
      },

      addCalendarNote(result, _args, cache) {
        const weeklyManagement = useWeekPlanningManagement();
        const formatedFirstDay = weeklyManagement.targetQueryStartDate;
        const formatedLastDay = weeklyManagement.targetQueryEndDate;
        const courseId = _args.createCalendarNoteInput.courseId;
        const calendarDayId = _args.createCalendarNoteInput.calendarDayId;

        cache.updateQuery(
          {
            query: CalendarDaysDocument,
            variables: {
              calendarDaysInput: {
                courseId: courseId,
                fromDay: formatedFirstDay,
                toDay: formatedLastDay,
              },
            },
          },
          (data) => {
            if (data) {
              const targetIndex = data.calendarDays.findIndex(
                (day) => day.id === calendarDayId,
              );
              data.calendarDays[targetIndex] = {
                ...data.calendarDays[targetIndex],
                calendarEvents: [
                  ...data.calendarDays[targetIndex].calendarEvents,
                  {
                    __typename: 'CalendarNote',
                    typename: 'CalendarNote',
                    type: 'CalendarNote',
                    id: result.addCalendarNote.calendarNote.id,
                    note: result.addCalendarNote.calendarNote.note,
                    foregroundColor:
                      result.addCalendarNote.calendarNote.foregroundColor,
                    backgroundColor:
                      result.addCalendarNote.calendarNote.backgroundColor,
                  },
                ],
              };
            }

            return data;
          },
        );
      },

      moveCalendarEvent(result, _args, cache) {
        const weeklyManagement = useWeekPlanningManagement();
        const formatedFirstDay = weeklyManagement.targetQueryStartDate;
        const formatedLastDay = weeklyManagement.targetQueryEndDate;

        const position = _args.moveCalendarEventInput.position;
        const courseId = _args.moveCalendarEventInput.courseId;
        const fromDay = _args.moveCalendarEventInput.fromDay;
        const oldDayCalendarID = fromDay.split('-').join('') + courseId;
        const calendarId = _args.moveCalendarEventInput.calendarDayId;

        // update cache with new position

        cache.updateQuery(
          {
            query: CalendarDaysDocument,
            variables: {
              calendarDaysInput: {
                courseId: courseId,
                fromDay: formatedFirstDay,
                toDay: formatedLastDay,
              },
            },
          },
          (data) => {
            if (data) {
              const fromIndex = data.calendarDays.findIndex(
                (day) => day.id === oldDayCalendarID,
              );
              const toIndex = data.calendarDays.findIndex(
                (day) => day.id === calendarId,
              );

              const positionToDelete = data.calendarDays[
                fromIndex
              ].calendarEvents.findIndex(
                (ev) => ev.id === result.moveCalendarEvent.calendarEvent.id,
              );

              const itemToMove = {
                ...data.calendarDays[fromIndex].calendarEvents[
                  positionToDelete
                ],
                id: result.moveCalendarEvent.calendarEvent.id,
                dates: result.moveCalendarEvent.calendarEvent.dates,
              };

              data.calendarDays[fromIndex].calendarEvents.splice(
                positionToDelete,
                1,
              );

              if (data.calendarDays[toIndex]) {
                data.calendarDays[toIndex].calendarEvents.splice(
                  position,
                  0,
                  itemToMove,
                );
              }
            }

            return data;
          },
        );

        // TODO: update cached statistics
        // known: courseId; not known: topicInstanceId
      },

      removeCalendarEvent(result, _args, cache) {
        const calendarEventId = _args.removeCalendarEventInput.calendarEventId;

        const day = _args.removeCalendarEventInput.day;
        const courseId = _args.removeCalendarEventInput.courseId;
        cache.updateQuery(
          {
            query: CalendarDaysDocument,
            variables: {
              calendarDaysInput: {
                courseId: courseId,
                fromDay: day,
                toDay: day,
              },
            },
          },
          (data) => {
            if (data) {
              const targetIndex = 0;

              // remove date from itemInstance
              const instance = data.calendarDays[
                targetIndex
              ].calendarEvents.find((event) => event.id === calendarEventId);
              if (instance.__typename === 'ItemInstance') {
                const fragment = gql`
                  fragment _ on ItemInstance {
                    id
                    dates
                  }
                `;

                cache.writeFragment(fragment, {
                  id: calendarEventId,
                  dates: instance.dates.filter((date) => date !== day),
                });
              }

              // remove instance from list
              const clone = { ...data };
              clone.calendarDays[targetIndex].calendarEvents =
                clone.calendarDays[targetIndex].calendarEvents.filter(
                  (itemInstance) => itemInstance.id !== calendarEventId,
                );
              data = clone;
            }

            return data;
          },
        );
      },
    },
  },
};

export const useWeekPlanningManagement = defineStore(
  'weeklyPlanningManagement',
  () => {
    const featuresStore = useFeaturesManagement();
    featuresStore.init();
    const addCalendarEventMutation = useAddCalendarEventMutation();
    const addCalendarEventsMutation = useAddCalendarEventsMutation();
    const moveCalendarEventMutation = useMoveCalendarEventMutation();
    const removeCalendarEventMutation = useRemoveCalendarEventMutation();
    const addCalendarNoteMutation = useAddCalendarNoteMutation();
    const updateCalendarNoteMutation = useUpdateCalendarNoteMutation();
    const addItemToCalendarMutation = useAddItemToCalendarMutation();

    const targetQueryStartDate = ref('');
    const targetQueryEndDate = ref('');

    const { trackCalendarEventAddedToPeriod, trackCalendarEventMoved } =
      useTrack();

    const setTargetQueryKeys = (key: WeeklyPlanningQueryKeys) => {
      targetQueryStartDate.value = key.fromDay;
      targetQueryEndDate.value = key.toDay;
    };

    const addCalendarEvent = async (
      vars: AddCalendarEventInput,
      item: CalendarEvent,
      queryKey: WeeklyPlanningQueryKeys,
      page: 'timetable' | 'plan' = 'plan',
      disableOnboarding?: boolean,
    ) => {
      setTargetQueryKeys(queryKey);
      const itemInfo = {
        id: item.id,
      };
      const { data, error } = await addCalendarEventMutation.executeMutation({
        addCalendarEventInput: {
          ...vars,
        },
        itemInfo,
      });
      trackCalendarEventAddedToPeriod(vars.calendarEventDetails.type);
      if (!disableOnboarding) {
        try {
          await featuresStore.pushOnboardingProgressProperty(
            'createdCalendarEvent',
            true,
          );
        } catch (e) {
          console.log(e);
        }
      }
      if (error) {
        throw error;
      }

      return data?.addCalendarEvent.calendarEvent;
    };

    const addCalendarEvents = async (
      vars: AddCalendarEventsInput,
      queryKey: WeeklyPlanningQueryKeys,
      page: 'timetable' | 'plan' = 'plan',
      disableOnboarding?: boolean,
    ) => {
      setTargetQueryKeys(queryKey);
      const { data, error } = await addCalendarEventsMutation.executeMutation({
        addCalendarEventsInput: {
          ...vars,
        },
      });
      trackCalendarEventAddedToPeriod(CalendarEventType.Item, false);
      if (!disableOnboarding) {
        try {
          await featuresStore.pushOnboardingProgressProperty(
            'createdCalendarEvent',
            true,
          );
        } catch (e) {
          console.log(e);
        }
      }
      if (error) {
        throw error;
      }

      return data?.addCalendarEvents.calendarEvents;
    };

    const addCalendarNote = async (
      vars: CreateCalendarNoteInput,
      queryKey: WeeklyPlanningQueryKeys,
      page: 'timetable' | 'plan' = 'plan',
    ) => {
      setTargetQueryKeys(queryKey);
      trackCalendarEventAddedToPeriod(
        CalendarEventType.Note,
        false,
        vars.foregroundColor === '#000000',
      );

      const { data, error } = await addCalendarNoteMutation.executeMutation({
        createCalendarNoteInput: {
          ...vars,
        },
      });
      if (error) {
        throw error;
      }

      try {
        await featuresStore.pushOnboardingProgressProperty(
          'createdCalendarEvent',
          true,
        );
      } catch (e) {
        console.log(e);
      }

      return data?.addCalendarNote.calendarNote;
    };

    const moveCalendarEvent = async (
      vars: MoveCalendarEventInput,
      queryKey: WeeklyPlanningQueryKeys,
    ) => {
      setTargetQueryKeys(queryKey);
      trackCalendarEventMoved();
      const { data, error } = await moveCalendarEventMutation.executeMutation({
        moveCalendarEventInput: {
          ...vars,
        },
      });
      if (error) {
        throw error;
      }

      return data?.moveCalendarEvent.calendarEvent;
    };

    const removeCalendarEvent = async (
      vars: RemoveCalendarEventInput,
      queryKey: WeeklyPlanningQueryKeys,
    ) => {
      setTargetQueryKeys(queryKey);
      const { data, error } = await removeCalendarEventMutation.executeMutation(
        {
          removeCalendarEventInput: {
            ...vars,
          },
        },
      );
      if (error) {
        throw error;
      }

      return data?.removeCalendarEvent.done;
    };

    const updateCalendarNote = async (vars: UpdateCalendarNoteInput) => {
      const { data, error } = await updateCalendarNoteMutation.executeMutation({
        updateCalendarNoteInput: {
          ...vars,
        },
      });

      if (error) {
        throw error;
      }

      return data?.updateCalendarNote.calendarNote;
    };

    const addItemToCalendar = async (
      vars: AddItemToCalendarInput,
      queryKey: WeeklyPlanningQueryKeys,
    ) => {
      console.log('set queryKey', queryKey);
      setTargetQueryKeys(queryKey);
      const { data, error } = await addItemToCalendarMutation.executeMutation({
        addItemToCalendarInput: {
          ...vars,
        },
      });

      trackCalendarEventAddedToPeriod('library');

      if (error) {
        throw error;
      }

      return data?.addItemToCalendar.calendarEvent;
    };

    return {
      addCalendarEvent,
      addCalendarEvents,
      addCalendarNote,
      moveCalendarEvent,
      removeCalendarEvent,
      updateCalendarNote,
      addItemToCalendar,
      targetQueryStartDate,
      targetQueryEndDate,
    };
  },
);
